// IMPORTS

// External Libraries
import { defineStore } from 'pinia';

// Type - Read models
import type { LearnerDashboardReadModel } from '@c1/dashboard.types.read-models';

//enums
export enum ComponentType {
  All = 'all',
  PRACTICE_EXTRA = 'PracticeExtra',
  TEST = 'Test',
  EBOOK = 'Ebook',
  RESOURCE_BANK = 'ResourceBank',
  EXTERNAL_RESOURCE = 'ExternalResources',
  PRESENTATION_PLUS = 'PresentationPlus',
  TEST_GENERATOR = 'TestGenerator',
  DOWNLOADABLE_ASSET = 'DownloadableAsset'
}
interface ProductsStoreState {
  /**
   * Applicable for Teachers only
   * (not loaded for learners)
   *
   * All Umbrellas that are present in Cambridge One
   */
  umbrellas?: {
    classUmbrellas?: LearnerDashboardReadModel['umbrellas'];
  };
  /**
   *
   * Applicable for Learner only
   * (not loaded for teachers)
   *
   * Array of Umbrella UUIDs in the learner's
   * independent learning section
   */
  independentLearning?: LearnerDashboardReadModel['independentLearning'];

  /**
   * Applicable for Teachers & Learner
   *
   * For Learners:
   * This is a union of all components within all umbrellas associated
   * with their classes. This includes components which have been
   * specifically activated for the class, as well as components
   * that are part of the umbrella but have not been activated
   * yet. Components for any umbrella which have been activated but
   * not yet associated with any class are also included.
   *
   * For teachers:
   * All components to which the teacher has access to.
   */
  components?: LearnerDashboardReadModel['components'];

  /**
   * Applicable for integration learners only
   *
   * This is a flag to indicate if the integration user needs to activate some components
   *
   */
  isActivationNeeded?: boolean;
}

/**
 * Products STORE
 *
 * This store is responsible for managing the teaching training page data
 * It fetches all the umbrellas from the backend segregated to generic and teacher training types.
 *
 */
export const useLearnerProductsStore = defineStore('LearnerProducts', {
  // Internal state. Initialised as EMPTY
  state: (): ProductsStoreState => ({}),

  getters: {},

  actions: {
    /**
     * Reset the permissions to their initial state
     *
     * This is useful when the underlying data
     * is no longer valid or needs to be refreshed.
     */
    resetPermissions: function () {
      this.components = undefined;
    },
    /**
     * Reset the Products store to it's initial state
     */
    reset: function () {
      this.components = undefined;
      this.umbrellas = undefined;
      this.independentLearning = undefined;
    },

    loadClassUmbrellas: function (
      data: LearnerDashboardReadModel['umbrellas']
    ) {
      this.umbrellas = this.umbrellas || {};
      this.umbrellas.classUmbrellas = data;
    },

    updateClassUmbrellas: function (
      data: LearnerDashboardReadModel['umbrellas']
    ) {
      this.umbrellas = this.umbrellas || {};
      this.umbrellas.classUmbrellas = Object.assign(
        this.umbrellas.classUmbrellas ?? {},
        data
      );
    },

    loadComponents: function (data: LearnerDashboardReadModel['components']) {
      this.components = data;
    },

    load(ProductStoreState: ProductsStoreState) {
      Object.assign(this, ProductStoreState);
    }
  }
});
